/**
 * Sprint 17 T17.18 — Sentry browser SDK (lazy init).
 *
 * `@sentry/react` paketi mevcutsa ve `VITE_SENTRY_DSN` env'i set ise init edilir.
 * Aksi halde no-op (paket yüklü değilken bile import'lanabilir).
 *
 * Paketi yüklemek için (founder Mac action 11.15):
 *   pnpm --filter @custel/web-admin add @sentry/react
 *   echo "VITE_SENTRY_DSN=https://...@sentry.io/..." >> apps/web-admin/.env.local
 *
 * Güvenlik:
 *   - `beforeSend` hook'unda PII redact (TCKN, VKN, IBAN, JWT, password, idempotencyKey, ...)
 *   - URL query string'inde TCKN/VKN/iban param'ları '[REDACTED]' yapılır
 *   - Replay session sampling: 0% (KVKK md.6 — PII içeren ekranlar kayda alınmaz);
 *     sadece error-on-replay (%100 hata anında) — kullanıcı eylem trail'i için
 *   - tracesSampleRate: %10 (Sentry transaction quota tasarrufu)
 */

import { createLogger } from './logger';

type CaptureFn = (error: unknown, context?: Record<string, unknown>) => void;
type AddBreadcrumbFn = (breadcrumb: Record<string, unknown>) => void;
type SetUserFn = (user: { id: string; companyId?: string; role?: string } | null) => void;
type SetTagFn = (key: string, value: string) => void;

const log = createLogger('sentry');

let captureFn: CaptureFn = () => undefined;
let addBreadcrumbFn: AddBreadcrumbFn = () => undefined;
let setUserFn: SetUserFn = () => undefined;
let setTagFn: SetTagFn = () => undefined;

let initialized = false;

// PII alanları — beforeSend ve breadcrumb data'sında [REDACTED] yapılır.
const PII_KEYS = new Set([
  'tckn',
  'vkn',
  'iban',
  'pan',
  'cardnumber',
  'cvv',
  'password',
  'currentpassword',
  'newpassword',
  'accesstoken',
  'refreshtoken',
  'authorization',
  'idempotencykey',
  'providerref',
  'bankaccountiban',
  'phone',
  'firstname',
  'lastname',
  'dobiso',
  'birthplace',
]);

const PII_URL_PARAMS = ['tckn', 'vkn', 'iban', 'phone', 'tax_number'] as const;

/** Recursive redact — PII alanlarını [REDACTED] yapar. */
function redactObject(input: unknown, depth = 0): unknown {
  if (depth > 8) return '[MAX_DEPTH]';
  if (input == null) return input;
  if (typeof input === 'string') return redactString(input);
  if (Array.isArray(input)) return input.map((v) => redactObject(v, depth + 1));
  if (typeof input !== 'object') return input;

  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
    if (PII_KEYS.has(k.toLowerCase())) {
      out[k] = '[REDACTED]';
    } else {
      out[k] = redactObject(v, depth + 1);
    }
  }
  return out;
}

/** URL query string'inde PII parametrelerini maskele. */
function redactString(value: string): string {
  if (!value.includes('?')) return value;
  try {
    const url = new URL(value, 'https://placeholder.invalid');
    let touched = false;
    for (const p of PII_URL_PARAMS) {
      if (url.searchParams.has(p)) {
        url.searchParams.set(p, '[REDACTED]');
        touched = true;
      }
    }
    if (!touched) return value;
    return url.pathname + url.search;
  } catch {
    return value;
  }
}

type SentryEvent = {
  request?: { url?: string; query_string?: string; cookies?: string; data?: unknown; headers?: Record<string, string> };
  extra?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
  breadcrumbs?: Array<{ data?: unknown; message?: string }>;
};

function sanitizeEvent(event: SentryEvent): SentryEvent {
  // Request URL/query/body redact
  if (event.request) {
    if (event.request.url) event.request.url = redactString(event.request.url);
    if (event.request.query_string)
      event.request.query_string = redactString(`?${event.request.query_string}`).slice(1);
    if (event.request.data) event.request.data = redactObject(event.request.data);
    // Cookie + Authorization tamamen sil — JWT/CSRF token sızıntı riski
    if (event.request.headers) {
      delete event.request.headers['Cookie'];
      delete event.request.headers['cookie'];
      delete event.request.headers['Authorization'];
      delete event.request.headers['authorization'];
    }
    if (event.request.cookies) event.request.cookies = '[REDACTED]';
  }
  if (event.extra) event.extra = redactObject(event.extra) as Record<string, unknown>;
  if (event.contexts) event.contexts = redactObject(event.contexts) as Record<string, unknown>;
  // Breadcrumb data — `createLogger` zaten redact ediyor, ama defansif
  if (Array.isArray(event.breadcrumbs)) {
    event.breadcrumbs = event.breadcrumbs.map((bc) => ({
      ...bc,
      data: bc.data ? (redactObject(bc.data) as Record<string, unknown>) : bc.data,
    }));
  }
  return event;
}

interface SentryLike {
  init: (opts: Record<string, unknown>) => void;
  captureException: (err: unknown, ctx?: Record<string, unknown>) => void;
  addBreadcrumb: (bc: Record<string, unknown>) => void;
  setUser: (u: Record<string, unknown> | null) => void;
  setTag: (key: string, value: string) => void;
  browserTracingIntegration?: () => unknown;
  replayIntegration?: (opts?: Record<string, unknown>) => unknown;
}

export async function initSentry(): Promise<void> {
  if (initialized) return;
  initialized = true;

  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) {
    // Dev/test — Sentry'siz çalışır. createLogger console'a düşmeye devam eder.
    return;
  }

  try {
    // Vite static analizden gizle — paket yoksa import fail etmesin.
    const pkg = '@sentry/react';
    const mod: unknown = await import(/* @vite-ignore */ pkg).catch(() => null);
    if (!mod || typeof (mod as { init?: unknown }).init !== 'function') {
      log.warn('@sentry/react paketi bulunamadı — Sentry no-op modunda', { hint: 'pnpm add @sentry/react' });
      return;
    }
    const Sentry = mod as SentryLike;

    const integrations: unknown[] = [];
    if (typeof Sentry.browserTracingIntegration === 'function') {
      integrations.push(Sentry.browserTracingIntegration());
    }
    if (typeof Sentry.replayIntegration === 'function') {
      integrations.push(
        Sentry.replayIntegration({
          // KVKK md.6 — TCKN/IBAN içeren input'lar replay'de görünmesin
          maskAllText: true,
          blockAllMedia: true,
          maskAllInputs: true,
        }),
      );
    }

    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      release: import.meta.env.VITE_GIT_SHA ?? 'dev',
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
      replaysSessionSampleRate: 0.0, // KVKK — session-wide replay yok
      replaysOnErrorSampleRate: 1.0, // hata anında %100 (60s öncesini gönderir)
      integrations,
      beforeSend(event: SentryEvent) {
        return sanitizeEvent(event);
      },
      beforeBreadcrumb(breadcrumb: { data?: unknown; message?: string }) {
        if (breadcrumb.data) {
          breadcrumb.data = redactObject(breadcrumb.data) as Record<string, unknown>;
        }
        if (breadcrumb.message) {
          breadcrumb.message = redactString(breadcrumb.message);
        }
        return breadcrumb;
      },
    });

    captureFn = (err, ctx) => Sentry.captureException(err, ctx);
    addBreadcrumbFn = (bc) => Sentry.addBreadcrumb(bc);
    setUserFn = (u) => Sentry.setUser(u as Record<string, unknown> | null);
    setTagFn = (k, v) => Sentry.setTag(k, v);

    // Sprint 17 T17.18 — logger.ts global lookup hook (circular dep'i önler).
    // createLogger her log line'ı bunu çağırır → Sentry breadcrumb trail.
    (
      globalThis as unknown as {
        __custelSentryBreadcrumb?: (lvl: string, mod: string, m: string, d?: Record<string, unknown>) => void;
      }
    ).__custelSentryBreadcrumb = (level, module, message, data) => {
      addBreadcrumbFn({
        type: 'default',
        category: module,
        level,
        message,
        data: data ? (redactObject(data) as Record<string, unknown>) : undefined,
        timestamp: Date.now() / 1000,
      });
    };

    // window.Sentry referansını set et — ErrorBoundary fallback için
    (window as unknown as { Sentry?: SentryLike }).Sentry = Sentry;

    // Unhandled rejection + global error fallback
    window.addEventListener('unhandledrejection', (e) => {
      captureFn(e.reason, { source: 'unhandledrejection' });
    });

    log.info('Sentry initialized', { env: import.meta.env.MODE, release: import.meta.env.VITE_GIT_SHA ?? 'dev' });
  } catch (err) {
    // Sessizce geç — observability prod'da fail-soft.
    log.warn('Sentry init failed (no-op fallback)', { err });
  }
}

export function captureError(error: unknown, context?: Record<string, unknown>): void {
  captureFn(error, context ? { extra: redactObject(context) as Record<string, unknown> } : undefined);
  if (import.meta.env.DEV) {
    log.error('captureError', error, context);
  }
}

/** createLogger'dan çağrılır — log line'ları Sentry breadcrumb trail'ine ekler. */
export function addLogBreadcrumb(
  level: 'debug' | 'info' | 'warn' | 'error',
  module: string,
  message: string,
  data?: Record<string, unknown>,
): void {
  addBreadcrumbFn({
    type: 'default',
    category: module,
    level,
    message,
    data: data ? (redactObject(data) as Record<string, unknown>) : undefined,
    timestamp: Date.now() / 1000,
  });
}

/** Login sonrası çağır — Sentry event'lerine user kontekst ekler. */
export function setSentryUser(user: { id: string; companyId?: string; role?: string } | null): void {
  // Email/tckn/iban ASLA Sentry'ye gönderilmemeli — sadece UUID + role.
  setUserFn(user);
  if (user?.companyId) setTagFn('tenantId', user.companyId);
  if (user?.role) setTagFn('role', user.role);
}

/** Test/util helper — TS sanitizeEvent + redactObject erişimi için. */
export const _internal = { sanitizeEvent, redactObject, redactString };
